import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Campaign } from '@/mocks/campaigns';
import SAR from '@/components/feature/SAR';

interface DashboardCampaignsProps {
  campaigns: Campaign[];
}

const statusMap = {
  active: { label: 'نشطة', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'مكتملة', color: 'bg-stone-100 text-stone-600' },
  pending: { label: 'قيد المراجعة', color: 'bg-amber-100 text-amber-700' },
};

const DashboardCampaigns = ({ campaigns }: DashboardCampaignsProps) => {
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

  const getCampaignStatus = (c: Campaign): keyof typeof statusMap => {
    if (c.daysLeft <= 0) return 'completed';
    return 'active';
  };

  const filtered = campaigns.filter((c) => {
    if (filter === 'all') return true;
    return getCampaignStatus(c) === filter;
  });

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-black text-stone-800 text-base">حملاتي</h3>
        <Link
          to="/create"
          className="flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          حملة جديدة
        </Link>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'all', label: 'الكل' },
          { key: 'active', label: 'نشطة' },
          { key: 'completed', label: 'مكتملة' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as typeof filter)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              filter === tab.key
                ? 'bg-stone-800 text-white'
                : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Campaigns List */}
      <div className="space-y-3">
        {filtered.map((campaign) => {
          const progress = Math.min((campaign.raisedAmount / campaign.goalAmount) * 100, 100);
          const status = getCampaignStatus(campaign);
          const statusInfo = statusMap[status];

          return (
            <div key={campaign.id} className="flex gap-3 p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors">
              {/* Image */}
              <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0">
                <img
                  src={campaign.coverImage}
                  alt={campaign.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="text-xs font-bold text-stone-800 leading-tight line-clamp-2">{campaign.title}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </div>

                {/* Progress */}
                <div className="mb-1.5">
                  <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {/* Stats Row */}
                <div className="flex items-center gap-3 text-xs text-stone-500">
                  <span className="font-semibold text-stone-700"><SAR amount={campaign.raisedAmount} iconSize={11} /></span>
                  <span>•</span>
                  <span>{Math.round(progress)}%</span>
                  <span>•</span>
                  <span>{campaign.donorsCount} داعم</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-1 shrink-0">
                <Link
                  to={`/campaign/${campaign.id}`}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-stone-200 text-stone-500 hover:text-emerald-600 hover:border-emerald-300 transition-colors cursor-pointer"
                >
                  <i className="ri-eye-line text-xs"></i>
                </Link>
                <button className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-stone-200 text-stone-500 hover:text-stone-700 transition-colors cursor-pointer">
                  <i className="ri-share-line text-xs"></i>
                </button>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-8 text-stone-400">
            <i className="ri-inbox-line text-3xl mb-2 block"></i>
            <p className="text-sm">لا توجد حملات في هذه الفئة</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardCampaigns;
